import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Topbar from "../components/Topbar";
import FilterBar from "../components/controls/FilterBar";
import SearchInput from "../components/controls/SearchInput";
import SelectField from "../components/controls/SelectField";
import { AdminHotelRow, HotelType } from "../types/hotels";
import { listAdminHotels, deleteAdminHotel } from "../api/hotels";
import { switchParsing } from "../api/parsing";
import { listCompanies } from "../api/companies";
import { SCompanyRow } from "../types/companies";
import AdminHotelModal from "../components/hotels/AdminHotelModal";
import ParsingSettingsModal from "../components/hotels/ParsingSettingsModal";
import AiReportSettingsModal from "../components/hotels/AiReportSettingsModal";
import ConfirmDeleteModal from "../components/modals/ConfirmDeleteModal";
import { formatGuests } from "../utils/guests";
import { formatAiReportDays, normalizeAiReportDays } from "../utils/aiReports";
import {
  getHotelDisplaySettings,
  loadHotelsDisplaySettings,
  type HotelDisplaySettings,
} from "../utils/hotelDisplaySettings";
import { useDebouncedUrlQuery } from "../hooks/useDebouncedUrlQuery";
import {
  BedDouble,
  Building2,
  CalendarDays,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  ExternalLink,
  LayoutGrid,
  Loader2,
  MoonStar,
  Pencil,
  Power,
  PowerOff,
  Table,
  Trash2,
  Users,
} from "lucide-react";

const TYPE_OPTIONS: HotelType[] = [
  "Отель",
  "Сетевой отель",
  "Апартаменты",
  "Гостевой дом",
  "Коттедж, вилла",
];

type SortField = "id" | "type" | "company_name" | "created_at";
type SortDirection = "asc" | "desc";

const SORT_LABELS: Record<SortField, string> = {
  id: "ID",
  type: "Тип",
  company_name: "Компания",
  created_at: "Дата добавления",
};

function formatMinStay(minStay: number[] | null) {
  return minStay?.length ? minStay.join(", ") : "Не задан";
}

function formatParsingValue(item: AdminHotelRow) {
  if (item.parsing_status === 0) return "OFF";
  if (item.parsing_status === 1) {
    return typeof item.parsing_count === "number" ? item.parsing_count : "-";
  }
  return "-";
}

function getApiErrorMessage(error: any, fallback: string) {
  const apiError =
    error?.response?.data?.error ??
    error?.response?.data?.detail ??
    error?.response?.data?.message ??
    error?.message;

  if (typeof apiError === "string" && apiError.trim()) return apiError;
  if (apiError != null) {
    try {
      return JSON.stringify(apiError);
    } catch {
      return String(apiError);
    }
  }
  return fallback;
}

export default function AdminHotelsPage() {
  const [viewMode, setViewMode] = useState<"cards" | "table">(() => {
    if (typeof window === "undefined") return "cards";
    const saved = window.localStorage.getItem("admin-hotels:view");
    return saved === "table" ? "table" : "cards";
  });
  const [items, setItems] = useState<AdminHotelRow[]>([]);
  const [displaySettingsMap, setDisplaySettingsMap] = useState<Record<string, HotelDisplaySettings>>({});
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [switchingParsingHotelId, setSwitchingParsingHotelId] = useState<number | null>(null);

  const [orderBy, setOrderBy] = useState<SortField>("id");
  const [orderDir, setOrderDir] = useState<SortDirection>("desc");

  const [companies, setCompanies] = useState<SCompanyRow[]>([]);

  const [typeDraft, setTypeDraft] = useState<HotelType | "">("");
  const [companyDraft, setCompanyDraft] = useState<string>("");
  const [typeFilter, setTypeFilter] = useState<HotelType | "">("");
  const [companyFilter, setCompanyFilter] = useState<string>("");
  const [searchDraft, setSearchDraft, searchQuery] = useDebouncedUrlQuery();
  const requestIdRef = useRef(0);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"create" | "edit">("create");
  const [editingHotel, setEditingHotel] = useState<AdminHotelRow | null>(null);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletingHotel, setDeletingHotel] = useState<AdminHotelRow | null>(null);
  const [parsingModalHotelId, setParsingModalHotelId] = useState<number | null>(null);
  const [aiReportModalHotelId, setAiReportModalHotelId] = useState<number | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const data = await listCompanies({ page: 1, per_page: 200 });
        setCompanies(data.items);
      } catch (e) {
        console.error("Не удалось загрузить список компаний", e);
      }
    })();
  }, []);

  const fetchHotels = useCallback(
    async (currentPage: number, showLoading = true) => {
      const requestId = ++requestIdRef.current;
      if (showLoading) setLoading(true);
      setError("");
      try {
        const data = await listAdminHotels({
          page: currentPage,
          per_page: perPage,
          q: searchQuery || null,
          type: typeFilter ? (typeFilter as HotelType) : null,
          company_id: companyFilter ? Number(companyFilter) : null,
          order_by: orderBy,
          order_dir: orderDir,
        });
        if (requestId !== requestIdRef.current) return;
        const nextDisplaySettingsMap = await loadHotelsDisplaySettings(data.items);
        if (requestId !== requestIdRef.current) return;
        setItems(data.items);
        setDisplaySettingsMap(nextDisplaySettingsMap);
        setTotal(data.total);
      } catch (e: any) {
        if (requestId !== requestIdRef.current) return;
        setError(e?.response?.data?.detail || "Ошибка загрузки объектов размещения");
      } finally {
        if (showLoading && requestId === requestIdRef.current) setLoading(false);
      }
    },
    [companyFilter, orderBy, orderDir, perPage, searchQuery, typeFilter],
  );

  useEffect(() => {
    fetchHotels(page);
  }, [fetchHotels, page]);

  useEffect(() => {
    window.localStorage.setItem("admin-hotels:view", viewMode);
  }, [viewMode]);

  const companyOptions = useMemo(
    () =>
      [...companies]
        .sort((a, b) => a.company_name.localeCompare(b.company_name))
        .map((company) => ({
          label: company.company_name,
          value: String(company.id),
        })),
    [companies],
  );

  const typeOptions = useMemo(
    () => TYPE_OPTIONS.map((typeValue) => ({ label: typeValue, value: typeValue })),
    [],
  );

  const parsingModalHotel = useMemo(
    () => items.find((item) => item.id === parsingModalHotelId) ?? null,
    [items, parsingModalHotelId],
  );
  const aiReportModalHotel = useMemo(
    () => items.find((item) => item.id === aiReportModalHotelId) ?? null,
    [items, aiReportModalHotelId],
  );

  const pages = Math.max(1, Math.ceil(total / perPage));

  const uniqueCompaniesCount = useMemo(() => {
    const ids = new Set<string>();
    items.forEach((item) => {
      const key = item.company_id ? String(item.company_id) : item.company_name;
      if (key) ids.add(key);
    });
    return ids.size;
  }, [items]);

  const popularType = useMemo(() => {
    if (!items.length) return "";
    const counter = new Map<HotelType, number>();
    items.forEach((item) => {
      counter.set(item.type, (counter.get(item.type) || 0) + 1);
    });
    let topType: HotelType | "" = "";
    let maxCount = 0;
    counter.forEach((count, type) => {
      if (count > maxCount) {
        maxCount = count;
        topType = type;
      }
    });
    return topType;
  }, [items]);

  function resetFilters() {
    setTypeDraft("");
    setCompanyDraft("");
    setTypeFilter("");
    setCompanyFilter("");
    setPage(1);
  }

  function applyFilters() {
    setTypeFilter(typeDraft);
    setCompanyFilter(companyDraft);
    setPage(1);
  }

  function toggleSort(field: SortField) {
    setPage(1);
    const isSameField = orderBy === field;
    const nextDir: SortDirection = isSameField ? (orderDir === "asc" ? "desc" : "asc") : "asc";
    setOrderBy(field);
    setOrderDir(nextDir);
  }

  function renderSortIcon(field: SortField) {
    if (orderBy !== field) {
      return <ChevronsUpDown size={16} className="text-slate-400" />;
    }
    return orderDir === "asc" ? (
      <ChevronUp size={16} className="text-white" />
    ) : (
      <ChevronDown size={16} className="text-white" />
    );
  }

  async function handleDeleteConfirm() {
    if (!deletingHotel) {
      throw new Error("Не выбран объект размещения для удаления");
    }
    const nextPage = items.length === 1 && page > 1 ? page - 1 : page;
    await deleteAdminHotel(deletingHotel.id);
    if (nextPage === page) {
      await fetchHotels(page);
    } else {
      setPage(nextPage);
    }
  }

  async function handleParsingSwitch(item: AdminHotelRow) {
    if (item.parsing_status === null || item.group_id === null) return;

    setSwitchingParsingHotelId(item.id);
    setError("");
    try {
      const response = await switchParsing(
        item.group_id,
        item.parsing_status === 1 ? "disable" : "enable",
      );
      if (response.status !== "ok") {
        throw new Error(getApiErrorMessage({ response: { data: response } }, "Ошибка переключения парсинга"));
      }
      await fetchHotels(page, false);
    } catch (e: any) {
      setError(getApiErrorMessage(e, "Ошибка переключения парсинга"));
    } finally {
      setSwitchingParsingHotelId(null);
    }
  }

  const hasActiveFilters = Boolean(typeFilter || companyFilter);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 text-slate-900">
      <Topbar />

      <main className="mx-auto w-full max-w-[1200px] px-6 py-10 space-y-8">
        <section className="relative overflow-hidden rounded-3xl border border-white/70 bg-white/90 p-8 shadow-[0_18px_40px_rgba(15,23,42,0.08)] backdrop-blur">
          <div className="absolute inset-y-0 right-0 w-40 translate-x-10 rounded-full bg-gradient-to-b from-indigo-100 to-purple-100 blur-3xl opacity-60 pointer-events-none" />
          <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <div className="space-y-3">
              <h1 className="text-3xl font-bold text-slate-900">Объекты размещения</h1>
              <p className="max-w-2xl text-sm text-slate-500">
                Управляйте отелями клиентов, отслеживайте популярные типы объектов и держите реестр в актуальном состоянии
              </p>
            </div>
            <button
              onClick={() => {
                setModalMode("create");
                setEditingHotel(null);
                setModalOpen(true);
              }}
              className="inline-flex items-center gap-2 self-start rounded-2xl bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-3 text-sm font-semibold text-white shadow-[0_15px_30px_rgba(99,102,241,0.25)] transition hover:translate-y-[-1px] hover:brightness-105"
            >
              <span className="text-lg leading-none">+</span> Добавить объект
            </button>
          </div>
        </section>

        <section className="grid gap-4 sm:grid-cols-3">
          <div className="rounded-3xl border border-white/70 bg-white/90 p-5 shadow-[0_10px_30px_rgba(15,23,42,0.08)] backdrop-blur">
            <p className="text-xs uppercase tracking-wide text-slate-400">Всего объектов</p>
            <div className="mt-2 text-3xl font-semibold text-slate-900">{total}</div>
            <p className="mt-1 text-sm text-slate-500">С учётом активных фильтров и сортировки</p>
          </div>
          <div className="rounded-3xl border border-white/70 bg-white/90 p-5 shadow-[0_10px_30px_rgba(15,23,42,0.08)] backdrop-blur">
            <p className="text-xs uppercase tracking-wide text-slate-400">Компаний в выдаче</p>
            <div className="mt-2 text-3xl font-semibold text-slate-900">{uniqueCompaniesCount}</div>
            <p className="mt-1 text-sm text-slate-500">На текущей странице списка</p>
          </div>
          <div className="rounded-3xl border border-white/70 bg-white/90 p-5 shadow-[0_10px_30px_rgba(15,23,42,0.08)] backdrop-blur">
            <p className="text-xs uppercase tracking-wide text-slate-400">Популярный тип</p>
            <div className="mt-2 text-2xl font-semibold text-slate-900">
              {popularType || "—"}
            </div>
            <p className="mt-1 text-sm text-slate-500">По объектам на текущей странице</p>
          </div>
        </section>

        <AdminHotelModal
          open={modalOpen}
          mode={modalMode}
          initialHotel={modalMode === "edit" ? editingHotel ?? undefined : undefined}
          companies={companies}
          onClose={() => setModalOpen(false)}
          onSuccess={() => {
            setModalOpen(false);
            setEditingHotel(null);
            if (modalMode === "create") {
              if (page !== 1) {
                setPage(1);
              } else {
                fetchHotels(1);
              }
            } else {
              fetchHotels(page);
            }
          }}
        />

        <ConfirmDeleteModal
          open={deleteOpen}
          onClose={() => {
            setDeleteOpen(false);
            setDeletingHotel(null);
          }}
          onConfirm={async () => {
            await handleDeleteConfirm();
            setDeleteOpen(false);
            setDeletingHotel(null);
          }}
          title="Удаление объекта размещения"
          message="Вы точно хотите удалить объект размещения?"
        />

        <ParsingSettingsModal
          open={Boolean(parsingModalHotelId)}
          hotel={parsingModalHotel}
          onClose={() => setParsingModalHotelId(null)}
          onRefresh={() => fetchHotels(page, false)}
        />

        <AiReportSettingsModal
          open={Boolean(aiReportModalHotelId)}
          hotel={aiReportModalHotel}
          onClose={() => setAiReportModalHotelId(null)}
          onRefresh={() => fetchHotels(page, false)}
        />

        <section className="rounded-3xl border border-white/70 bg-white/90 p-5 shadow-[0_10px_30px_rgba(15,23,42,0.08)] backdrop-blur">
          <FilterBar
            onApply={applyFilters}
            onReset={resetFilters}
            hasActive={hasActiveFilters}
            applyDisabled={loading}
            className="mb-0"
            sticky={false}
            noWrap
          >
            <SelectField
              value={typeDraft}
              onChange={(value) => setTypeDraft(value as HotelType)}
              options={typeOptions}
              placeholder="Тип"
              icon={<BedDouble size={18} />}
              className="min-w-[180px]"
            />
            <SelectField
              value={companyDraft}
              onChange={(value) => setCompanyDraft(String(value))}
              options={companyOptions}
              placeholder="Компания"
              icon={<Building2 size={18} />}
              className="min-w-[220px]"
            />
          </FilterBar>
        </section>

        <section className="grid gap-3 rounded-3xl border border-indigo-100 bg-gradient-to-r from-white via-indigo-50/70 to-white p-4 shadow-[0_12px_30px_rgba(99,102,241,0.08)] md:grid-cols-[minmax(0,1fr)_auto] md:items-center">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-indigo-500">Поиск объектов</p>
            <SearchInput
              value={searchDraft}
              onChange={(value) => {
                setSearchDraft(value);
                setPage(1);
              }}
              placeholder="Поиск по названию"
              variant="soft"
              className="mt-2"
            />
          </div>
          <div className="rounded-2xl border border-white/80 bg-white/80 px-4 py-3 text-sm text-slate-500 shadow-sm md:text-right">
            <div className="text-xs uppercase tracking-wide text-slate-400">В выдаче</div>
            <div className="mt-1 text-xl font-semibold text-slate-900">{total.toLocaleString("ru-RU")}</div>
          </div>
        </section>

        <section className="rounded-3xl border border-white/60 bg-white/80 p-4 shadow-sm backdrop-blur flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm text-slate-500">Сортировка:</span>
            {(Object.keys(SORT_LABELS) as SortField[]).map((field) => {
              const isActive = orderBy === field;
              return (
                <button
                  key={field}
                  type="button"
                  onClick={() => toggleSort(field)}
                  className={[
                    "inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-sm transition",
                    isActive
                      ? "border-slate-900 bg-slate-900 text-white shadow-lg shadow-slate-900/20"
                      : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50",
                  ].join(" ")}
                >
                  {SORT_LABELS[field]}
                  <span className={isActive ? "text-white" : ""}>{renderSortIcon(field)}</span>
                </button>
              );
            })}
          </div>
          <div className="inline-flex items-center rounded-2xl border border-slate-200 bg-white p-1 shadow-sm">
            <button
              type="button"
              onClick={() => setViewMode("cards")}
              className={`inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold transition ${
                viewMode === "cards" ? "bg-indigo-600 text-white shadow-sm" : "text-slate-600 hover:bg-slate-50"
              }`}
            >
              <LayoutGrid size={16} />
              Плашки
            </button>
            <button
              type="button"
              onClick={() => setViewMode("table")}
              className={`inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold transition ${
                viewMode === "table" ? "bg-indigo-600 text-white shadow-sm" : "text-slate-600 hover:bg-slate-50"
              }`}
            >
              <Table size={16} />
              Таблица
            </button>
          </div>
        </section>

        {error && (
          <div className="rounded-3xl border border-amber-200 bg-amber-50/90 px-5 py-4 text-sm text-amber-800 shadow-inner">
            {error}
          </div>
        )}

        {loading && (
          <div className="rounded-3xl border border-white/70 bg-white/90 px-6 py-12 text-center text-slate-500 shadow-[0_12px_30px_rgba(15,23,42,0.08)] backdrop-blur">
            <div className="inline-flex items-center gap-2 text-sm">
              <Loader2 className="animate-spin text-indigo-600" size={18} />
              Загрузка объектов размещения…
            </div>
          </div>
        )}

        {!loading && items.length === 0 && (
          <div className="rounded-3xl border border-white/70 bg-white/90 px-6 py-12 text-center text-slate-500 shadow-[0_12px_30px_rgba(15,23,42,0.08)] backdrop-blur">
            {searchQuery ? "Ничего не найдено" : "Объекты размещения не найдены"}
          </div>
        )}

        {!loading && items.length > 0 && viewMode === "cards" && (
          <div className="space-y-4">
            {items.map((item) => {
              const displaySettings = getHotelDisplaySettings(displaySettingsMap, item);
              const aiReportDays = normalizeAiReportDays(item);
              return (
                <div
                  key={item.id}
                  className="overflow-hidden rounded-3xl border border-white/70 bg-white/95 p-6 shadow-[0_20px_45px_rgba(15,23,42,0.1)] transition hover:-translate-y-[1px] hover:shadow-[0_25px_55px_rgba(15,23,42,0.12)]"
                >
                <div className="grid grid-cols-1 gap-6 md:grid-cols-[minmax(0,2.2fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,0.8fr)_minmax(0,1fr)_minmax(0,1.2fr)_minmax(0,1.6fr)] md:items-center">
                  <div>
                    <div className="text-xs uppercase tracking-wide text-slate-400">Название</div>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <span className="text-lg font-semibold text-slate-900">
                        {item.name || "—"}
                      </span>
                      <span className="inline-flex items-center rounded-full border border-slate-200 px-3 py-1 text-xs text-slate-500">
                        ID: {item.id}
                      </span>
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-2 text-sm text-slate-500">
                      <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1">
                        <Building2 size={14} className="text-slate-500" />
                        {item.company_name || "—"}
                      </span>
                      <a
                        href={item.url || "#"}
                        target={item.url ? "_blank" : undefined}
                        rel={item.url ? "noopener noreferrer" : undefined}
                        className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-3 py-1 text-indigo-700 transition hover:bg-indigo-100"
                      >
                        <ExternalLink size={14} />
                        <span className="max-w-[220px] truncate">{item.url || "—"}</span>
                      </a>
                    </div>
                  </div>

                  <div>
                    <div className="text-xs uppercase tracking-wide text-slate-400">Тип</div>
                    <div className="mt-3 inline-flex items-center gap-2 rounded-2xl bg-slate-50 px-3 py-1.5 text-sm text-slate-700">
                      <BedDouble size={16} className="text-slate-500" />
                      {item.type || "—"}
                    </div>
                  </div>

                  <div>
                    <div className="text-xs uppercase tracking-wide text-slate-400">Мин. stay</div>
                    <div className="mt-3 inline-flex items-center gap-2 rounded-2xl bg-amber-50 px-3 py-1.5 text-sm text-amber-800">
                      <MoonStar size={16} className="text-amber-600" />
                      {formatMinStay(displaySettings.min_stay)}
                    </div>
                  </div>

                  <div>
                    <div className="text-xs uppercase tracking-wide text-slate-400">Гостей</div>
                    <div className="mt-3 inline-flex items-center gap-2 rounded-2xl bg-indigo-50 px-3 py-1.5 text-sm text-indigo-800">
                      <Users size={16} className="text-indigo-600" />
                      {formatGuests(displaySettings.guests, displaySettings.guests_child)}
                    </div>
                  </div>

                  <div>
                    <div className="text-xs uppercase tracking-wide text-slate-400">
                      Компания
                    </div>
                    <div className="mt-3 text-sm font-semibold text-slate-800">
                      {item.company_name || "—"}
                    </div>
                  </div>

                  <div>
                    <div className="text-xs uppercase tracking-wide text-slate-400">
                      Кол-во ОТА
                    </div>
                    <div className="mt-3 text-sm font-semibold text-slate-800">
                      {item.ota_count ?? 3}
                    </div>
                  </div>

                  <div>
                    <div className="text-xs uppercase tracking-wide text-slate-400">
                      Парсинг
                    </div>
                    <button
                      type="button"
                      onClick={() => setParsingModalHotelId(item.id)}
                      className="mt-3 inline-flex items-center rounded-2xl border border-indigo-100 bg-indigo-50 px-3 py-1.5 text-sm font-semibold text-indigo-700 transition hover:bg-indigo-100"
                    >
                      {formatParsingValue(item)}
                    </button>
                  </div>

                  <div>
                    <div className="text-xs uppercase tracking-wide text-slate-400">
                      ИИ отчеты
                    </div>
                    <button
                      type="button"
                      onClick={() => setAiReportModalHotelId(item.id)}
                      className={`mt-3 inline-flex max-w-full items-center whitespace-normal break-words rounded-2xl border px-3 py-1.5 text-left text-sm font-semibold leading-5 transition ${
                        aiReportDays.length
                          ? "border-cyan-100 bg-cyan-50 text-cyan-700 hover:bg-cyan-100"
                          : "border-slate-200 bg-white text-slate-500 hover:bg-slate-50"
                      }`}
                    >
                      {formatAiReportDays(aiReportDays)}
                    </button>
                  </div>

                  <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-end md:gap-6">
                    <div>
                      <div className="text-xs uppercase tracking-wide text-slate-400">
                        Дата добавления
                      </div>
                      <div className="mt-2 inline-flex items-center gap-2 rounded-full bg-slate-50 px-3 py-1.5 text-sm text-slate-600">
                        <CalendarDays size={16} className="text-slate-400" />
                        {item.created_at
                          ? new Date(item.created_at).toLocaleDateString("ru-RU")
                          : "—"}
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center justify-end gap-2">
                      {item.parsing_status !== null && item.group_id !== null && (
                        <button
                          type="button"
                          onClick={() => handleParsingSwitch(item)}
                          disabled={switchingParsingHotelId === item.id}
                          aria-label={item.parsing_status === 1 ? "Выключить парсинг" : "Включить парсинг"}
                          title={item.parsing_status === 1 ? "Выключить парсинг" : "Включить парсинг"}
                          className="inline-flex items-center justify-center rounded-2xl border border-indigo-200 bg-indigo-50 p-2 text-indigo-700 transition hover:bg-indigo-100 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {switchingParsingHotelId === item.id ? (
                            <Loader2 size={18} className="animate-spin" />
                          ) : item.parsing_status === 1 ? (
                            <PowerOff size={18} />
                          ) : (
                            <Power size={18} />
                          )}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          setModalMode("edit");
                          setEditingHotel(item);
                          setModalOpen(true);
                        }}
                        className="inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white p-2 text-slate-600 transition hover:bg-slate-50"
                      >
                        <Pencil size={18} />
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setDeletingHotel(item);
                          setDeleteOpen(true);
                        }}
                        className="inline-flex items-center justify-center rounded-2xl border border-red-200 bg-red-50 p-2 text-red-600 transition hover:bg-red-100"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                </div>
                </div>
              );
            })}
          </div>
        )}

        {!loading && items.length > 0 && viewMode === "table" && (
          <div className="overflow-hidden rounded-3xl border border-white/70 bg-white/95 shadow-[0_20px_45px_rgba(15,23,42,0.1)]">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm text-slate-600">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-400">
                  <tr>
                    <th className="px-3 py-4">Объект</th>
                    <th className="px-3 py-4">Компания</th>
                    <th className="px-3 py-4">Тип</th>
                    <th className="px-3 py-4">Мин. stay</th>
                    <th className="px-3 py-4">Гостей</th>
                    <th className="px-3 py-4">Кол-во ОТА</th>
                    <th className="px-3 py-4">Парсинг</th>
                    <th className="px-3 py-4">ИИ отчеты</th>
                    <th className="px-3 py-4">Дата добавления</th>
                    <th className="px-3 py-4 text-right">Действия</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {items.map((item) => {
                    const displaySettings = getHotelDisplaySettings(displaySettingsMap, item);
                    const aiReportDays = normalizeAiReportDays(item);
                    return (
                    <tr key={item.id} className="transition hover:bg-slate-50/70">
                      <td className="px-3 py-4">
                        <div className="flex items-center gap-2">
                          <div className="font-semibold text-slate-900">{item.name || "—"}</div>
                          {item.url ? (
                            <a
                              href={item.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              aria-label={`Открыть ссылку объекта ${item.name || item.id}`}
                              title={item.url}
                              className="inline-flex items-center justify-center rounded-xl border border-indigo-100 bg-indigo-50 p-1.5 text-indigo-700 transition hover:bg-indigo-100"
                            >
                              <ExternalLink size={14} />
                            </a>
                          ) : null}
                        </div>
                        <div className="text-xs uppercase tracking-wide text-slate-400">ID {item.id}</div>
                      </td>
                      <td className="px-3 py-4">
                        <div className="inline-flex items-center gap-2 text-sm text-slate-700">
                          <Building2 size={16} className="text-slate-500" />
                          {item.company_name || "—"}
                        </div>
                      </td>
                      <td className="px-3 py-4">
                        <div className="inline-flex items-center gap-2 text-sm text-slate-700">
                          <BedDouble size={16} className="text-slate-500" />
                          {item.type || "—"}
                        </div>
                      </td>
                      <td className="px-3 py-4">
                        <div className="inline-flex items-center gap-2 text-sm text-slate-700">
                          <MoonStar size={16} className="text-amber-600" />
                          {formatMinStay(displaySettings.min_stay)}
                        </div>
                      </td>
                      <td className="px-3 py-4">
                        <div className="inline-flex items-center gap-2 text-sm text-slate-700">
                          <Users size={16} className="text-indigo-600" />
                          {formatGuests(displaySettings.guests, displaySettings.guests_child)}
                        </div>
                      </td>
                      <td className="px-3 py-4">
                        <div className="text-sm font-semibold text-slate-800">{item.ota_count ?? 3}</div>
                      </td>
                      <td className="px-3 py-4">
                        <button
                          type="button"
                          onClick={() => setParsingModalHotelId(item.id)}
                          className="inline-flex items-center rounded-2xl border border-indigo-100 bg-indigo-50 px-3 py-1.5 text-sm font-semibold text-indigo-700 transition hover:bg-indigo-100"
                        >
                          {formatParsingValue(item)}
                        </button>
                      </td>
                      <td className="px-3 py-4">
                        <button
                          type="button"
                          onClick={() => setAiReportModalHotelId(item.id)}
                          className={`inline-flex items-center rounded-2xl border px-3 py-1.5 text-sm font-semibold transition ${
                            aiReportDays.length
                              ? "border-cyan-100 bg-cyan-50 text-cyan-700 hover:bg-cyan-100"
                              : "border-slate-200 bg-white text-slate-500 hover:bg-slate-50"
                          }`}
                        >
                          {formatAiReportDays(aiReportDays)}
                        </button>
                      </td>
                      <td className="px-3 py-4">
                        <div className="inline-flex items-center gap-2 text-sm text-slate-700">
                          <CalendarDays size={16} className="text-slate-500" />
                          {item.created_at
                            ? new Date(item.created_at).toLocaleDateString("ru-RU")
                            : "—"}
                        </div>
                      </td>
                      <td className="px-3 py-4">
                        <div className="flex flex-wrap justify-end gap-2">
                          {item.parsing_status !== null && item.group_id !== null && (
                            <button
                              type="button"
                              onClick={() => handleParsingSwitch(item)}
                              disabled={switchingParsingHotelId === item.id}
                              aria-label={item.parsing_status === 1 ? "Выключить парсинг" : "Включить парсинг"}
                              title={item.parsing_status === 1 ? "Выключить парсинг" : "Включить парсинг"}
                              className="inline-flex items-center justify-center rounded-2xl border border-indigo-200 bg-indigo-50 p-2 text-indigo-700 transition hover:bg-indigo-100 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              {switchingParsingHotelId === item.id ? (
                                <Loader2 size={18} className="animate-spin" />
                              ) : item.parsing_status === 1 ? (
                                <PowerOff size={18} />
                              ) : (
                                <Power size={18} />
                              )}
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => {
                              setModalMode("edit");
                              setEditingHotel(item);
                              setModalOpen(true);
                            }}
                            className="inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white p-2 text-slate-600 transition hover:bg-slate-50"
                          >
                            <Pencil size={18} />
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setDeletingHotel(item);
                              setDeleteOpen(true);
                            }}
                            className="inline-flex items-center justify-center rounded-2xl border border-red-200 bg-red-50 p-2 text-red-600 transition hover:bg-red-100"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <section className="flex flex-wrap items-center gap-4 rounded-3xl border border-white/70 bg-white/90 p-5 shadow-[0_10px_30px_rgba(15,23,42,0.08)] backdrop-blur">
          <div className="flex items-center gap-2">
            <label className="text-sm text-slate-600" htmlFor="admin-hotels-per-page">
              На странице:
            </label>
            <select
              id="admin-hotels-per-page"
              value={perPage}
              onChange={(e) => {
                const next = Number(e.target.value);
                setPerPage(next);
                setPage(1);
              }}
              className="rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-inner"
            >
              {[10, 20, 50, 100].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center justify-center gap-2 sm:ml-auto">
            <button
              onClick={() => setPage((current) => Math.max(1, current - 1))}
              disabled={page <= 1 || loading}
              className="inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
            >
              Назад
            </button>
            <div className="rounded-2xl bg-slate-100 px-3 py-2 text-sm font-semibold text-slate-800">
              {page} / {pages}
            </div>
            <button
              onClick={() => setPage((current) => Math.min(pages, current + 1))}
              disabled={page >= pages || loading}
              className="inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
            >
              Вперёд
            </button>
          </div>
        </section>
      </main>
    </div>
  );
}
